import I18n from '@/i18n';
import type { MediaclipUploadedPhoto, MediaclipUploadSession } from '@/types/DirectUpload';
import type { UploadFailure, UploadResponse } from '@/types/Upload';
import ProductPageMediaclipProjectController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/ProductPageMediaclipProjectController';
import ProductPageUploadSessionController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/ProductPageUploadSessionController';
import { getCsrfToken } from '@/utils/csrf';
import { DirectUploadError, photoRejectionKey, uploadToMediaclip, waitForPhotoProcessing } from '@/utils/mediaclipUpload';
import type { Ref } from 'vue';
import { ref } from 'vue';

/** The transfer owns 0-90%; a timer eases the tail toward 99% until the project exists. */
const TRANSFER_CEILING = 90;
const PROCESSING_CEILING = 99;
const TAIL_TICK_MS = 100;
const TAIL_HALF_LIFE_BASE_MS = 3000;
const TAIL_HALF_LIFE_PER_MEGABYTE_MS = 250;
const BYTES_PER_MEGABYTE = 1024 * 1024;

export type DirectFileUpload = {
    isUploading: Ref<boolean>;
    uploadProgress: Ref<number>;
    uploadFile: (file: File, sku: string) => Promise<UploadResponse | null>;
    prefetchSession: () => void;
    cancelUpload: () => void;
    onUploadError: (handler: (failure: UploadFailure) => void) => void;
};

function sameOriginJsonRequest(method: 'GET' | 'POST', headers: Record<string, string> = {}): RequestInit {
    return {
        method,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-XSRF-TOKEN': getCsrfToken(), ...headers },
    };
}

function genericErrorMessage(): string {
    return I18n.global.t('pages.product_detail_page.product_info.product_button.upload.errors.generic');
}

async function requestSession(): Promise<MediaclipUploadSession> {
    const response = await fetch(ProductPageUploadSessionController.url(), sameOriginJsonRequest('GET'));

    if (!response.ok) {
        throw new DirectUploadError('Could not start the upload session', response.status);
    }

    return (await response.json()) as MediaclipUploadSession;
}

/** Only the controller's own failures carry status 'error'; framework errors (419, 429, 404) are not translated. */
async function createProject(sku: string, photo: MediaclipUploadedPhoto, signal: AbortSignal): Promise<UploadResponse> {
    const response = await fetch(ProductPageMediaclipProjectController.url({ product: sku }), {
        ...sameOriginJsonRequest('POST', { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ photo_id: photo.photoId, photo_url: photo.photoUrl, filename: photo.filename }),
        signal,
    });

    const payload = (await response.json()) as UploadResponse | { status?: 'error'; message?: string };

    if (!response.ok) {
        const customerMessage = payload.status === 'error' ? payload.message : undefined;

        throw new DirectUploadError('Could not create the Mediaclip project', response.status, customerMessage);
    }

    return payload as UploadResponse;
}

function tailHalfLifeMs(fileSizeBytes: number): number {
    return TAIL_HALF_LIFE_BASE_MS + (fileSizeBytes / BYTES_PER_MEGABYTE) * TAIL_HALF_LIFE_PER_MEGABYTE_MS;
}

function isRetryableTransferError(error: unknown): boolean {
    return error instanceof DirectUploadError && error.status === undefined;
}

function failureFrom(error: unknown): UploadFailure {
    if (error instanceof DirectUploadError) {
        return { message: error.customerMessage ?? genericErrorMessage(), status: error.status };
    }

    return { message: genericErrorMessage() };
}

class DirectUploader {
    public readonly isUploading = ref<boolean>(false);
    public readonly uploadProgress = ref<number>(0);
    private errorHandler: ((failure: UploadFailure) => void) | null = null;
    private sessionRequest: Promise<MediaclipUploadSession> | undefined = undefined;
    private abortController: AbortController | undefined = undefined;
    private tailTicker: ReturnType<typeof setInterval> | undefined = undefined;

    /**
     * Started when the file dialog opens so the round trip overlaps with the user picking a
     * file. The rejection is absorbed here as well, because the user may never pick one.
     */
    public prefetchSession(): void {
        const request = requestSession();
        this.sessionRequest = request;

        request.catch(() => {
            this.sessionRequest = undefined;
        });
    }

    public async uploadFile(file: File, sku: string): Promise<UploadResponse | null> {
        const rejectionKey = photoRejectionKey(file);

        if (rejectionKey) {
            this.emitFailure({ message: I18n.global.t(rejectionKey) });

            return null;
        }

        this.isUploading.value = true;
        this.uploadProgress.value = 0;
        this.abortController = new AbortController();

        try {
            return await this.runUpload(file, sku, this.abortController.signal);
        } catch (error: unknown) {
            if (!this.isAborted()) {
                this.emitFailure(failureFrom(error));
            }

            return null;
        } finally {
            this.stopTailProgress();
            this.isUploading.value = false;
            this.sessionRequest = undefined;
        }
    }

    /** Aborts the transfer or the processing poll that is in flight; the upload then settles as null. */
    public cancelUpload(): void {
        this.abortController?.abort();
        this.stopTailProgress();
        this.isUploading.value = false;
        this.uploadProgress.value = 0;
    }

    public onUploadError(handler: (failure: UploadFailure) => void): void {
        this.errorHandler = handler;
    }

    private isAborted(): boolean {
        return this.abortController?.signal.aborted ?? false;
    }

    private session(): Promise<MediaclipUploadSession> {
        if (!this.sessionRequest) {
            this.prefetchSession();
        }

        return this.sessionRequest!;
    }

    private async runUpload(file: File, sku: string, signal: AbortSignal): Promise<UploadResponse> {
        const uploadSession = await this.session();

        const photo = await this.transferWithSingleRetry(file, uploadSession, signal);

        this.startTailProgress(file.size);
        await waitForPhotoProcessing(uploadSession, photo, file.size, signal);

        const result = await createProject(sku, photo, signal);
        this.uploadProgress.value = 100;

        return result;
    }

    /**
     * A transfer that fails on the network, rather than being refused by Mediaclip with a
     * status, is retried once.
     */
    private async transferWithSingleRetry(file: File, uploadSession: MediaclipUploadSession, signal: AbortSignal): Promise<MediaclipUploadedPhoto> {
        const onProgress = (percentage: number): void => this.reportTransferProgress(percentage);

        try {
            return await uploadToMediaclip(file, uploadSession, onProgress, signal);
        } catch (error: unknown) {
            if (signal.aborted || !isRetryableTransferError(error)) {
                throw error;
            }

            this.uploadProgress.value = 0;

            return uploadToMediaclip(file, uploadSession, onProgress, signal);
        }
    }

    private reportTransferProgress(percentage: number): void {
        this.uploadProgress.value = Math.floor((percentage * TRANSFER_CEILING) / 100);
    }

    /**
     * Mediaclip's processing and the project call report no bytes, so a timer eases the tail
     * toward its ceiling instead: each half-life halves the remaining gap, and the bar keeps
     * moving however long those steps really take.
     */
    private startTailProgress(fileSizeBytes: number): void {
        const startedAt = Date.now();
        const halfLifeMs = tailHalfLifeMs(fileSizeBytes);

        this.stopTailProgress();
        this.tailTicker = setInterval(() => this.reportTailProgress(Date.now() - startedAt, halfLifeMs), TAIL_TICK_MS);
    }

    private stopTailProgress(): void {
        if (this.tailTicker === undefined) {
            return;
        }

        clearInterval(this.tailTicker);
        this.tailTicker = undefined;
    }

    private reportTailProgress(elapsedMs: number, halfLifeMs: number): void {
        if (!this.isUploading.value) {
            return;
        }

        const share = 1 - Math.pow(2, -elapsedMs / halfLifeMs);
        const tailSpan = PROCESSING_CEILING - TRANSFER_CEILING;

        this.uploadProgress.value = Math.max(this.uploadProgress.value, Math.floor(TRANSFER_CEILING + share * tailSpan));
    }

    private emitFailure(failure: UploadFailure): void {
        if (this.errorHandler) {
            this.errorHandler(failure);
        }
    }
}

export function useDirectFileUpload(): DirectFileUpload {
    const uploader = new DirectUploader();

    return {
        isUploading: uploader.isUploading,
        uploadProgress: uploader.uploadProgress,
        uploadFile: (file: File, sku: string) => uploader.uploadFile(file, sku),
        prefetchSession: () => uploader.prefetchSession(),
        cancelUpload: () => uploader.cancelUpload(),
        onUploadError: (handler: (failure: UploadFailure) => void) => uploader.onUploadError(handler),
    };
}
